/**
 * GoalBlox - Educational Gaming Platform
 * 
 * Main App entry with landing page for first-time visitors
 */

import { useState, useEffect } from 'react';
import QuestHub from './pages/QuestHub';
import ParentDashboard from './pages/ParentDashboard';
import { LandingPage, WelcomeBackBanner } from './pages/LandingPage';
import { ProofQuestSelector, ProofQuest } from './components/ProofQuest';
import { type ProofProblem } from './lib/proofs';

type View = 'landing' | 'quests' | 'proofs' | 'parent';

// Check if user has visited before
const VISITED_KEY = 'goalblox_visited';
const USER_KEY = 'goalblox_user';

function getInitialView(): View {
  if (typeof window !== 'undefined') {
    const hasVisited = localStorage.getItem(VISITED_KEY);
    return hasVisited ? 'quests' : 'landing';
  }
  return 'landing';
}

function getUser(): { name: string } | null {
  if (typeof window !== 'undefined') {
    const user = localStorage.getItem(USER_KEY);
    return user ? JSON.parse(user) : null;
  }
  return null;
}

export default function App() {
  const [view, setView] = useState<View>(getInitialView);
  const [activeProofProblem, setActiveProofProblem] = useState<ProofProblem | null>(null);
  const [completedProofs, setCompletedProofs] = useState<string[]>([]);
  const [showWelcomeBack, setShowWelcomeBack] = useState(false);
  const [user, setUser] = useState<{ name: string } | null>(null);

  // Check for returning user
  useEffect(() => {
    const savedUser = getUser();
    if (savedUser) {
      setUser(savedUser);
      // Show welcome back banner if they land on homepage
      if (view === 'landing') {
        setTimeout(() => setShowWelcomeBack(true), 1000);
      }
    }
  }, []);

  const handleEnterApp = () => {
    // Mark as visited
    localStorage.setItem(VISITED_KEY, 'true');
    setView('quests');
    setShowWelcomeBack(false);
  };

  const handleParentSignup = () => {
    // For now, just enter the app (would go to signup flow)
    localStorage.setItem(VISITED_KEY, 'true');
    localStorage.setItem(USER_KEY, JSON.stringify({ name: 'Parent' }));
    setUser({ name: 'Parent' });
    setView('parent');
  };

  const handleProofComplete = (result: { score: number; passed: boolean; xpEarned: number }) => {
    if (result.passed && activeProofProblem) {
      setCompletedProofs(prev => [...prev, activeProofProblem.id]);
    }
    setTimeout(() => {
      setActiveProofProblem(null);
    }, 2000);
  };

  // Landing page for first-time visitors
  if (view === 'landing') {
    return (
      <>
        <LandingPage 
          onEnterApp={handleEnterApp}
          onParentSignup={handleParentSignup}
          isReturningUser={!!user}
        />
        {showWelcomeBack && user && (
          <WelcomeBackBanner
            userName={user.name}
            onContinue={handleEnterApp}
            onViewLanding={() => setShowWelcomeBack(false)}
          />
        )}
      </>
    );
  }

  return (
    <div className="min-h-screen bg-blox-darker text-white">
      {/* Navigation */}
      <nav className="fixed top-4 right-4 z-50 flex gap-2">
        <button
          onClick={() => setView('landing')}
          className="px-3 py-2 rounded-xl text-sm bg-blox-surface text-slate-400 hover:bg-blox-card transition-all"
          title="Home"
        >
          🏠
        </button>
        <button
          onClick={() => { setView('quests'); setActiveProofProblem(null); }}
          className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
            view === 'quests' 
              ? 'bg-blox-accent text-blox-dark' 
              : 'bg-blox-surface text-slate-300 hover:bg-blox-card'
          }`}
        >
          🎮 Quest Hub
        </button>
        <button
          onClick={() => { setView('proofs'); setActiveProofProblem(null); }}
          className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
            view === 'proofs' 
              ? 'bg-blox-accent text-blox-dark' 
              : 'bg-blox-surface text-slate-300 hover:bg-blox-card'
          }`}
        >
          🔍 Proof Quest
        </button>
        <button
          onClick={() => setView('parent')}
          className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
            view === 'parent' 
              ? 'bg-blox-accent text-blox-dark' 
              : 'bg-blox-surface text-slate-300 hover:bg-blox-card'
          }`}
        >
          👨‍👩‍👧 Parent
        </button>
      </nav>

      {/* View content */}
      {view === 'quests' && <QuestHub />}
      
      {view === 'proofs' && (
        <div className="pt-16 px-4 pb-8">
          {activeProofProblem ? (
            <ProofQuest
              problem={activeProofProblem}
              childId="demo_user"
              onComplete={handleProofComplete}
            />
          ) : (
            <ProofQuestSelector
              onSelectProblem={setActiveProofProblem}
              completedProblems={completedProofs}
            />
          )}
        </div>
      )}
      
      {view === 'parent' && <ParentDashboard />}
    </div>
  );
}
